import { type AttackerData, attackerSize } from '../entities/Attacker';
import { MAX_STACK_SLOTS } from '../config/constants';

/**
 * Manages the waiting/stack slots where attackers go after completing a conveyor lap.
 * The player can tap any attacker in the stack to dispatch it back onto the conveyor.
 * When the stack overflows (all slots occupied and another attacker needs to enter), the game is lost.
 *
 * Capacity is configurable per-level via the constructor (defaults to MAX_STACK_SLOTS).
 */
export class StackSystem {
  private slots: (AttackerData | null)[];
  private readonly capacity: number;

  constructor(capacity: number = MAX_STACK_SLOTS) {
    this.capacity = capacity;
    this.slots = Array.from({ length: capacity }, () => null);
  }

  /** The configured capacity (number of slots) for this stack. */
  getCapacity(): number {
    return this.capacity;
  }

  /**
   * Find contiguous empty slots of the given size.
   * Returns the starting index, or -1 if none found.
   */
  private findContiguousEmpty(size: number): number {
    for (let i = 0; i <= this.capacity - size; i++) {
      let allEmpty = true;
      for (let j = 0; j < size; j++) {
        if (this.slots[i + j] !== null) {
          allEmpty = false;
          break;
        }
      }
      if (allEmpty) return i;
    }
    return -1;
  }

  /** Add an attacker to the stack. Returns false if no room. */
  addToStack(attacker: AttackerData): boolean {
    const startIdx = this.findContiguousEmpty(attackerSize(attacker));
    if (startIdx === -1) return false;
    attacker.state = 'waiting';
    for (let j = 0; j < attackerSize(attacker); j++) {
      this.slots[startIdx + j] = attacker;
    }
    return true;
  }

  /** Dispatch an attacker from the stack back to the conveyor. */
  dispatch(slotIndex: number): AttackerData | null {
    const attacker = this.slots[slotIndex];
    if (!attacker) return null;
    attacker.state = 'on_conveyor';
    for (let i = 0; i < this.capacity; i++) {
      if (this.slots[i] === attacker) {
        this.slots[i] = null;
      }
    }
    this.compact();
    return attacker;
  }

  /** Shift all waiting attackers left so there are no gaps. */
  private compact(): void {
    const attackers: AttackerData[] = [];
    const seen = new Set<number>();
    for (let i = 0; i < this.capacity; i++) {
      const a = this.slots[i];
      if (a && !seen.has(a.id)) {
        seen.add(a.id);
        attackers.push(a);
      }
    }
    this.slots.fill(null);
    let idx = 0;
    for (const a of attackers) {
      for (let j = 0; j < attackerSize(a); j++) {
        this.slots[idx + j] = a;
      }
      idx += attackerSize(a);
    }
  }

  /** Number of occupied slot entries (including multi-slot attackers). */
  count(): number {
    return this.slots.filter((s) => s !== null).length;
  }

  /** Number of empty slot entries. */
  emptySlotCount(): number {
    return this.slots.filter((s) => s === null).length;
  }

  /** Returns true when all slots are full (game over condition). */
  isFull(): boolean {
    return this.slots.every((s) => s !== null);
  }

  /** Get the slots array (read-only). */
  getSlots(): readonly (AttackerData | null)[] {
    return this.slots;
  }
}
